import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  BarChart3, Eye, Star, MessageSquare, TrendingUp, 
  ArrowLeft, Calendar, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ModelAnalytics = () => {
  const { modelId, editToken } = useParams();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [modelId]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      // Verify edit token first
      await axios.get(`${API}/models/${modelId}/edit/${editToken}`);
      
      // Fetch analytics
      const response = await axios.get(`${API}/admin/model-analytics/${modelId}`);
      setAnalytics(response.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      if (err.response?.status === 404) {
        setError('Invalid access token or model not found.');
      } else {
        setError('Failed to load analytics. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, subValue, color = "red" }) => (
    <div className="bg-zinc-900 rounded-lg p-6 border border-red-900/20">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-full bg-${color}-600/20 flex items-center justify-center`}>
          <Icon className={`text-${color}-500`} size={20} />
        </div>
        <span className="text-gray-400 text-sm">{label}</span>
      </div>
      <p className="text-3xl font-bold text-white">{value}</p>
      {subValue && <p className="text-gray-500 text-sm mt-1">{subValue}</p>}
    </div>
  );

  if (loading) {
    return (
      <div className="bg-black text-white min-h-screen pt-32 pb-20 flex justify-center items-center">
        <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-black text-white min-h-screen pt-32 pb-20">
        <div className="container mx-auto px-4 text-center">
          <BarChart3 className="mx-auto text-red-500 mb-4" size={48} />
          <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
          <p className="text-gray-400 mb-8">{error}</p>
          <Link 
            to="/freelance-models"
            className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors"
          >
            <ArrowLeft size={18} /> Back to Models
          </Link>
        </div>
      </div>
    );
  }

  const { model, stats, rating_distribution, daily_views } = analytics || {};

  // Calculate max views for chart scaling
  const maxDailyViews = Math.max(...(daily_views?.map(d => d.views) || [1]), 1);

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-20">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link 
              to={`/models/${modelId}/edit/${editToken}`}
              className="inline-flex items-center gap-2 text-gray-400 hover:text-red-400 transition-colors mb-4"
            >
              <ArrowLeft size={18} /> Back to Edit Profile
            </Link>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-white">
              {model?.name}'s <span className="text-red-500">Analytics</span>
            </h1>
            <p className="text-gray-400 mt-2">Track your profile performance and engagement</p>
          </div>
          <button
            onClick={fetchAnalytics}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            data-testid="refresh-analytics"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
          <StatCard 
            icon={Eye} 
            label="Total Views" 
            value={stats?.total_views?.toLocaleString() || 0} 
          />
          <StatCard 
            icon={TrendingUp} 
            label="This Week" 
            value={stats?.weekly_views?.toLocaleString() || 0}
            color="green"
          />
          <StatCard 
            icon={Calendar} 
            label="This Month" 
            value={stats?.monthly_views?.toLocaleString() || 0}
            color="blue"
          />
          <StatCard 
            icon={Star} 
            label="Avg Rating" 
            value={stats?.average_rating || 0}
            subValue={`${stats?.total_reviews || 0} reviews`}
            color="yellow"
          />
          <StatCard 
            icon={MessageSquare} 
            label="Enquiries" 
            value={stats?.total_enquiries || 0}
            color="purple"
          />
          <div className="bg-zinc-900 rounded-lg p-6 border border-red-900/20">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-green-600/20 flex items-center justify-center">
                <TrendingUp className="text-green-500" size={20} />
              </div>
              <span className="text-gray-400 text-sm">Status</span>
            </div>
            <p className={`text-lg font-bold ${model?.status === 'approved' ? 'text-green-400' : 'text-yellow-400'}`}>
              {model?.status === 'approved' ? 'Live' : model?.status}
            </p>
            {model?.is_featured && (
              <p className="text-yellow-400 text-sm mt-1 flex items-center gap-1">
                <Star size={12} fill="currentColor" /> Featured
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Daily Views Chart - Last 30 Days */}
          <div className="bg-zinc-900 rounded-lg p-6 border border-red-900/20">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Calendar size={20} className="text-red-500" /> Profile Views (Last 30 Days)
            </h2>
            <div className="overflow-x-auto">
              <div className="flex items-end gap-1 h-48 min-w-[600px]">
                {daily_views?.map((day, idx) => {
                  const height = (day.views / maxDailyViews) * 100;
                  const isToday = idx === daily_views.length - 1;
                  return (
                    <div 
                      key={idx} 
                      className="flex-1 flex flex-col items-center group cursor-pointer"
                      title={`${day.date}: ${day.views} views`}
                    >
                      <span className="text-gray-500 text-xs opacity-0 group-hover:opacity-100 transition-opacity mb-1">
                        {day.views}
                      </span>
                      <div 
                        className={`w-full rounded-t transition-all ${
                          isToday ? 'bg-red-500' : 'bg-red-600/60 hover:bg-red-500'
                        }`}
                        style={{ height: `${Math.max(height, 2)}%` }}
                      />
                      {idx % 5 === 0 && (
                        <span className="text-gray-600 text-xs mt-1 transform -rotate-45 origin-top-left">
                          {day.date.slice(5)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <p className="text-gray-500 text-xs mt-4 text-center">Hover over bars to see exact view counts</p>
          </div>

          {/* Rating Distribution */}
          <div className="bg-zinc-900 rounded-lg p-6 border border-red-900/20">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Star size={20} className="text-yellow-500" /> Rating Distribution
            </h2>
            {stats?.total_reviews > 0 ? (
              <div className="space-y-4">
                {[5, 4, 3, 2, 1].map(rating => {
                  const count = rating_distribution?.[rating] || 0;
                  const percentage = stats?.total_reviews > 0 
                    ? (count / stats.total_reviews) * 100 
                    : 0;
                  return (
                    <div key={rating} className="flex items-center gap-3">
                      <div className="flex items-center gap-1 w-20">
                        <span className="text-white font-medium">{rating}</span>
                        <Star size={14} className="text-yellow-400 fill-yellow-400" />
                      </div>
                      <div className="flex-grow h-4 bg-zinc-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-yellow-500 to-yellow-400 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-gray-400 text-sm w-12 text-right">{count}</span>
                    </div>
                  );
                })}
                <div className="mt-6 pt-4 border-t border-zinc-800">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Average Rating</span>
                    <div className="flex items-center gap-2">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map(star => (
                          <Star 
                            key={star} 
                            size={18} 
                            className={star <= Math.round(stats?.average_rating || 0) 
                              ? 'text-yellow-400 fill-yellow-400' 
                              : 'text-gray-600'
                            } 
                          />
                        ))}
                      </div>
                      <span className="text-white font-bold text-lg">{stats?.average_rating}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Star className="mx-auto text-gray-600 mb-4" size={40} />
                <p className="text-gray-400">No reviews yet</p>
                <p className="text-gray-500 text-sm mt-2">
                  Share your profile to get client reviews
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Profile Info Summary */}
        <div className="bg-zinc-900 rounded-lg p-6 border border-red-900/20">
          <h2 className="text-xl font-semibold text-white mb-6">Profile Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <p className="text-gray-500 text-sm mb-1">Categories</p>
              <div className="flex flex-wrap gap-1">
                {model?.categories?.length > 0 ? (
                  model.categories.map(cat => (
                    <span key={cat} className="bg-zinc-800 text-gray-300 px-2 py-1 rounded text-sm">
                      {cat}
                    </span>
                  ))
                ) : (
                  <span className="text-gray-500">None set</span>
                )}
              </div>
            </div>
            <div>
              <p className="text-gray-500 text-sm mb-1">Instagram</p>
              <p className="text-white">
                {model?.instagram_url ? (
                  <a href={model.instagram_url} target="_blank" rel="noopener noreferrer" className="text-red-400 hover:text-red-300">
                    View Profile
                  </a>
                ) : (
                  <span className="text-gray-500">Not set</span>
                )}
              </p>
            </div>
            <div>
              <p className="text-gray-500 text-sm mb-1">Portfolio Video</p>
              <p className="text-white">{model?.video_url ? 'Uploaded' : 'Not uploaded'}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm mb-1">Images</p>
              <p className="text-white">{model?.image_urls?.length || 0} / 4</p>
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="mt-8 bg-red-900/10 rounded-lg p-6 border border-red-600/30">
          <h3 className="text-lg font-semibold text-white mb-4">💡 Tips to Boost Your Profile</h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-300">
            <li className="flex items-start gap-2">
              <span className="text-red-400">•</span>
              Upload all 4 photos to showcase variety in your work
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-400">•</span>
              Add a portfolio video to stand out from other models
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-400">•</span>
              Share your profile on social media to increase views
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-400">•</span>
              Ask satisfied clients to leave reviews
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ModelAnalytics;
